import { ScrollReveal } from "@/components/base/ScrollAnimations";
import { piicServices } from "@/mocks/piicData";

interface CellFocusProps {
  focus: string[];
  description: string;
}

const serviceIcons = [
  "ri-user-search-line",
  "ri-briefcase-5-line",
  "ri-team-line",
  "ri-hand-heart-line",
  "ri-microscope-line",
  "ri-bar-chart-box-line",
  "ri-shield-check-line",
  "ri-exchange-line",
];

export default function CellFocus({ focus, description }: CellFocusProps) {
  return (
    <section className="bg-white py-16 md:py-24 lg:py-32">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          {/* Focus Areas */}
          <ScrollReveal>
            <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-tie-teal/10 mb-4 md:mb-6">
                <span className="text-xs font-semibold text-tie-teal uppercase tracking-wider">
                  / Focus Areas
                </span>
              </div>
              <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl font-bold text-tie-text-primary leading-tight">
                What This Cell Solves
              </h2>
              <p className="mt-3 md:mt-4 text-base md:text-lg text-tie-text-secondary">
                {description}
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5 max-w-4xl mx-auto mb-16 md:mb-24">
            {focus.map((item, index) => (
              <ScrollReveal key={item} delay={index * 100}>
                <div className="group p-5 md:p-6 rounded-xl bg-tie-grey-light border border-tie-grey-dark hover:border-tie-teal/30 hover:shadow-lg transition-all duration-300">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-tie-teal flex items-center justify-center shrink-0">
                      <i className="ri-checkbox-circle-line text-white text-base" />
                    </div>
                    <p className="text-sm md:text-base font-medium text-tie-text-primary leading-snug pt-2">
                      {item}
                    </p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>

          {/* Services */}
          <ScrollReveal delay={200}>
            <div className="text-center max-w-3xl mx-auto mb-8 md:mb-12">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-tie-navy/10 mb-4 md:mb-5">
                <span className="text-xs font-semibold text-tie-navy uppercase tracking-wider">
                  / What Every Cell Delivers
                </span>
              </div>
              <h3 className="font-serif text-xl md:text-2xl lg:text-3xl font-bold text-tie-text-primary leading-tight">
                Services and Activities
              </h3>
              <p className="mt-3 md:mt-4 text-base md:text-lg text-tie-text-secondary">
                Beyond technical specialisation, every cell delivers a consistent suite of services that drive capability building, knowledge exchange, and operational excellence across the ecosystem.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {piicServices.map((service, index) => (
              <ScrollReveal key={service} delay={index * 50}>
                <div className="group p-4 md:p-5 rounded-xl bg-tie-grey-light border border-tie-grey-dark hover:border-tie-navy/30 hover:shadow-md transition-all duration-300">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-tie-navy/10 flex items-center justify-center shrink-0 group-hover:bg-tie-navy transition-colors duration-300">
                      <i className={`${serviceIcons[index]} text-tie-navy text-sm group-hover:text-white transition-colors duration-300`} />
                    </div>
                    <p className="text-sm font-medium text-tie-text-primary leading-snug pt-1.5">
                      {service}
                    </p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}